import React, { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import {
  Alert, Box, Button, Card, FormControl, Grid, InputLabel, LinearProgress, MenuItem, Select, Slider,
  Stack, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography, useTheme,
} from '@mui/material';
import { CloudUpload, NetworkCheck, PlayArrow, Stop } from '@mui/icons-material';
import { httpClient } from '@/utils/httpClient';
import {
  BlockHeading, DataTable, DatagridEmpty, KpiTile, PageTitle, SectionHeader, StatusDot,
} from '@/components/shell';
import { useStatFormat } from '@/features/statistics/accountHealth';
import { useStressTest } from '../hooks/useStressTest';
import type { StressTestResult, StressTestScenario } from '../types';

const SCENARIO_KEYS: Record<StressTestScenario, string> = {
  'gateway-health': 'tech_support.stress.scenarios.gateway_health',
  'admin-api-query': 'tech_support.stress.scenarios.admin_api',
  'ocr-processing': 'tech_support.stress.scenarios.ocr',
};

/** Scenarios that can be started. `admin-api-query` measured the panel itself through the
 *  gateway and is off on the server; it stays in SCENARIO_KEYS to label past runs. */
const STARTABLE_SCENARIOS: StressTestScenario[] = ['gateway-health', 'ocr-processing'];

/** Concurrent requests the API accepts per test (StressTest:MaxConcurrentRequests). */
const MAX_CONCURRENT_REQUESTS = 20;

/** Above this share of failed requests a run is marked as a problem. */
const HISTORY_ERROR_THRESHOLD = 5;

const ms = (value: number) => `${value} ms`;

/** How many requests fell in each latency bucket: columns from zero on one scale, the count
 *  written on each column so the tooltip is never the only copy. */
const LatencyDistribution: React.FC<{ result: StressTestResult }> = ({ result }) => {
  const t = useTranslate();
  const theme = useTheme();
  const fmt = useStatFormat();
  const maxCount = Math.max(...result.latencyDistribution.map((b) => b.count), 1);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('tech_support.stress.latency_distribution')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 140 }}>
        {result.latencyDistribution.map((bucket) => (
          <Tooltip key={bucket.bucket} title={t('screens.stressTest.bucketTooltip', { bucket: bucket.bucket, count: fmt.count(bucket.count) })}>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmt.count(bucket.count)}</Typography>
              <Box
                sx={{
                  width: '60%',
                  maxWidth: 40,
                  height: `${(bucket.count / maxCount) * 80}%`,
                  minHeight: 2,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: '2px 2px 0 0',
                }}
              />
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, borderTop: `1px solid ${theme.palette.divider}`, pt: 0.5 }}>
        {result.latencyDistribution.map((bucket) => (
          <Typography key={bucket.bucket} variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0, textAlign: 'center', overflowWrap: 'anywhere' }}>
            {bucket.bucket}
          </Typography>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
        {t('screens.stressTest.bucketAxis')}
      </Typography>
    </Box>
  );
};

export const StressTestPanel: React.FC = () => {
  const t = useTranslate();
  const theme = useTheme();
  const fmt = useStatFormat();
  const { config, setConfig, progress, result, history, isRunning, startTest, cancelTest, error } = useStressTest();
  const [accounts, setAccounts] = useState<Array<{ accountName: string }>>([]);
  const [showHistory, setShowHistory] = useState(false);

  // Load accounts for the OCR scenario
  useEffect(() => {
    if (config.scenario === 'ocr-processing') {
      httpClient.get<any>('/admin/account/GetAccounts?draw=1&length=100&start=0&search=&column=&orderBy=asc')
        .then(r => setAccounts(r.data?.data?.map((a: any) => ({ accountName: a.accountResponse?.accountName || a.accountName })) || []))
        .catch(() => {});
    }
  }, [config.scenario]);

  const progressPercent = progress
    ? Math.min((progress.elapsedSeconds / progress.totalDurationSeconds) * 100, 100)
    : 0;

  return (
    <Box>
      <SectionHeader
        left={<PageTitle title={t('tech_support.tabs.stress_test')} subtitle={t('screens.stressTest.subtitle')} />}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('tech_support.stress.disclaimer')}
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('screens.stressTest.startFailed', { error })}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Box component="section" aria-labelledby="stress-config">
            <BlockHeading id="stress-config" title={t('tech_support.stress.config_title')} />
            <Card sx={{ p: 2 }}>
              <Stack spacing={2.5}>
                <FormControl fullWidth>
                  <InputLabel id="stress-scenario-label">{t('tech_support.stress.scenario')}</InputLabel>
                  <Select
                    labelId="stress-scenario-label"
                    value={config.scenario}
                    label={t('tech_support.stress.scenario')}
                    onChange={(e) => setConfig(prev => ({ ...prev, scenario: e.target.value as StressTestScenario }))}
                    disabled={isRunning}
                  >
                    {STARTABLE_SCENARIOS.map((key) => (
                      <MenuItem key={key} value={key}>{t(SCENARIO_KEYS[key])}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {config.scenario === 'ocr-processing' && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel id="stress-account-label">{t('tech_support.stress.account')}</InputLabel>
                      <Select
                        labelId="stress-account-label"
                        value={config.accountName || ''}
                        label={t('tech_support.stress.account')}
                        onChange={(e) => setConfig(prev => ({ ...prev, accountName: e.target.value }))}
                        disabled={isRunning}
                      >
                        {accounts.map(a => (
                          <MenuItem key={a.accountName} value={a.accountName}>{a.accountName}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {t('tech_support.stress.test_image')}
                      </Typography>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        disabled={isRunning}
                        fullWidth
                        sx={{ justifyContent: 'flex-start' }}
                      >
                        {config.testImageBase64
                          ? t('tech_support.stress.image_loaded')
                          : t('tech_support.stress.upload_image')}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = () => {
                              const loaded = reader.result as string;
                              // Strip the data:image/...;base64, prefix
                              const base64 = loaded.includes(',') ? loaded.split(',')[1] : loaded;
                              setConfig(prev => ({ ...prev, testImageBase64: base64 }));
                            };
                            reader.readAsDataURL(file);
                          }}
                        />
                      </Button>
                      {config.testImageBase64 && (
                        <Box sx={{ mt: 0.5 }}>
                          <StatusDot tone="ok" label={t('tech_support.stress.image_ready')} />
                        </Box>
                      )}
                    </Box>
                  </>
                )}

                <Box sx={{ px: 1 }}>
                  <Typography id="stress-concurrency" variant="body2" color="text.secondary" gutterBottom>
                    {t('tech_support.stress.concurrent')}: <Box component="span" sx={{ color: 'text.primary' }}>{config.concurrentRequests}</Box>
                  </Typography>
                  <Slider
                    aria-labelledby="stress-concurrency"
                    value={config.concurrentRequests}
                    onChange={(_, v) => setConfig(prev => ({ ...prev, concurrentRequests: v as number }))}
                    min={1} max={MAX_CONCURRENT_REQUESTS} step={1}
                    disabled={isRunning}
                    marks={[{ value: 1, label: '1' }, { value: 10, label: '10' }, { value: MAX_CONCURRENT_REQUESTS, label: String(MAX_CONCURRENT_REQUESTS) }]}
                  />
                </Box>

                <Box sx={{ px: 1 }}>
                  <Typography id="stress-duration" variant="body2" color="text.secondary" gutterBottom>
                    {t('tech_support.stress.duration')}: <Box component="span" sx={{ color: 'text.primary' }}>{config.durationSeconds} s</Box>
                  </Typography>
                  <Slider
                    aria-labelledby="stress-duration"
                    value={config.durationSeconds}
                    onChange={(_, v) => setConfig(prev => ({ ...prev, durationSeconds: v as number }))}
                    min={5} max={120} step={5}
                    disabled={isRunning}
                    marks={[{ value: 5, label: '5 s' }, { value: 60, label: '60 s' }, { value: 120, label: '120 s' }]}
                  />
                </Box>

                {!isRunning ? (
                  <Button
                    variant="contained"
                    startIcon={<PlayArrow />}
                    onClick={startTest}
                    disabled={config.scenario === 'ocr-processing' && (!config.accountName || !config.testImageBase64)}
                    fullWidth
                  >
                    {t('tech_support.stress.start')}
                  </Button>
                ) : (
                  <Button variant="contained" color="error" startIcon={<Stop />} onClick={cancelTest} fullWidth>
                    {t('tech_support.stress.cancel')}
                  </Button>
                )}
              </Stack>
            </Card>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          <Stack spacing={3}>
            {progress && isRunning && (
              <Box component="section" aria-labelledby="stress-progress">
                <BlockHeading id="stress-progress" title={t('tech_support.stress.progress_title')} />
                <Card sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {progress.elapsedSeconds} s / {progress.totalDurationSeconds} s
                    </Typography>
                    <Typography variant="body2">{Math.round(progressPercent)} %</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={progressPercent} aria-label={t('tech_support.stress.progress_title')} />
                </Card>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <KpiTile label={t('tech_support.stress.completed')} value={fmt.count(progress.completedRequests)} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KpiTile label={t('tech_support.stress.errors')} value={fmt.count(progress.errors)} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KpiTile label={t('screens.stressTest.rps')} value={progress.currentRps} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <KpiTile label={t('tech_support.stress.avg_latency')} value={ms(progress.avgLatencyMs)} />
                  </Grid>
                </Grid>
              </Box>
            )}

            {result && !isRunning && (
              <Box component="section" aria-labelledby="stress-results">
                <BlockHeading id="stress-results" title={t('tech_support.stress.results_title')} />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={4}>
                    <KpiTile label={t('screens.stressTest.rps')} value={result.requestsPerSecond} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <KpiTile label={t('screens.stressTest.avg')} value={ms(result.latency.avg)} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <KpiTile
                      label={t('tech_support.stress.error_rate')}
                      value={fmt.rate(result.errorRate)}
                      detail={t('screens.stressTest.rateBase', { failed: fmt.count(result.failedRequests), total: fmt.count(result.totalRequests) })}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <KpiTile label="p50" value={ms(result.latency.p50)} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <KpiTile label="p95" value={ms(result.latency.p95)} />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <KpiTile label="p99" value={ms(result.latency.p99)} />
                  </Grid>
                </Grid>

                <Card sx={{ p: 2, mb: 2 }}>
                  <LatencyDistribution result={result} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mt: 2, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('tech_support.stress.total_requests')}: <Box component="span" sx={{ color: 'text.primary' }}>{fmt.count(result.totalRequests)}</Box>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('tech_support.stress.successful')}: <Box component="span" sx={{ color: 'text.primary' }}>{fmt.count(result.successfulRequests)}</Box>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('tech_support.stress.failed')}: <Box component="span" sx={{ color: result.failedRequests > 0 ? theme.custom.status.problem.text : 'text.primary' }}>{fmt.count(result.failedRequests)}</Box>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('screens.stressTest.minMax')}: <Box component="span" sx={{ color: 'text.primary' }}>{ms(result.latency.min)} / {ms(result.latency.max)}</Box>
                    </Typography>
                  </Box>
                </Card>

                {result.errors.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('tech_support.stress.error_breakdown')}</Typography>
                    <DataTable label={t('tech_support.stress.error_breakdown')}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('screens.stressTest.statusCode')}</TableCell>
                          <TableCell align="right">{t('tech_support.stress.count')}</TableCell>
                          <TableCell>{t('tech_support.stress.message')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.errors.map((err) => (
                          <TableRow key={`${err.statusCode}-${err.message}`}>
                            <TableCell>{err.statusCode || t('screens.stressTest.network')}</TableCell>
                            <TableCell align="right">{fmt.count(err.count)}</TableCell>
                            <TableCell>{err.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </DataTable>
                  </>
                )}
              </Box>
            )}

            {!isRunning && !result && (
              <Box component="section" aria-labelledby="stress-results-empty">
                <BlockHeading id="stress-results-empty" title={t('tech_support.stress.results_title')} />
                <Card>
                  <DatagridEmpty
                    icon={<NetworkCheck />}
                    title={t('tech_support.stress.placeholder')}
                    hint={t('screens.stressTest.placeholderHint')}
                  />
                </Card>
              </Box>
            )}

            {history.length > 0 && (
              <Box component="section" aria-labelledby="stress-history">
                <BlockHeading
                  id="stress-history"
                  title={t('tech_support.stress.history_title')}
                  subtitle={t('screens.stressTest.historyCount', { smart_count: history.length })}
                  action={(
                    <Button
                      variant="outlined"
                      onClick={() => setShowHistory((v) => !v)}
                      aria-expanded={showHistory}
                      aria-controls="stress-history-table"
                    >
                      {t(showHistory ? 'screens.stressTest.hideHistory' : 'screens.stressTest.showHistory')}
                    </Button>
                  )}
                />
                {showHistory && (
                  <Box id="stress-history-table">
                    <DataTable label={t('tech_support.stress.history_title')}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('tech_support.stress.history_date')}</TableCell>
                          <TableCell>{t('tech_support.stress.scenario')}</TableCell>
                          <TableCell align="right">{t('screens.stressTest.rps')}</TableCell>
                          <TableCell align="right">p95</TableCell>
                          <TableCell>{t('tech_support.stress.error_rate')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((h) => (
                          <TableRow key={h.testId}>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(h.startedAt).toLocaleString()}</TableCell>
                            <TableCell>{t(SCENARIO_KEYS[h.scenario] || h.scenario)}</TableCell>
                            <TableCell align="right">{h.requestsPerSecond}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{ms(h.latency.p95)}</TableCell>
                            <TableCell>
                              <StatusDot tone={h.errorRate > HISTORY_ERROR_THRESHOLD ? 'problem' : 'ok'} label={fmt.rate(h.errorRate)} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </DataTable>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
